import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { Task } from '@lit/task';
import * as d3 from 'd3';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { linkStyles } from './cssstyles.js';

@customElement('sparkline-chart')
export class SparklineChart extends LitElement {
  
  @property({ attribute: false }) metrics?: WireTypes.DayMetrics[];
  @property({ attribute: false }) timezone?: string;

  @query("svg") private _chart?: SVGElement;

  private _dataTask = new Task(this, {
    args: () => [this.metrics] as const,
    task: async ([allMetrics], { signal }) => {
      CHECK(allMetrics);
      const dayToMetric = new Map<string, WireTypes.DayMetrics>();
      for (const metric of allMetrics)
        dayToMetric.set(metric.day, metric);

      const today = Temporal.Now.plainDateISO(this.timezone);
      const since = today.subtract({ days: 30 });
      const data = []
      for (let d = since; Temporal.PlainDate.compare(d, today) <= 0; d = d.add({ days: 1 }))
        data.push(dayToMetric.get(d.toString())?.runsCount ?? 0)
      return data;
    },
  });

  protected override updated(changedProperties: PropertyValues): void {
    this._updateChart();
  }

  override render() {
    return html`
      <sl-resize-observer @sl-resize=${() => this._updateChart() }>
        <svg id=chart width=120 height=25>
          <defs>
            <linearGradient id=gradient x1="0" x2="0" y1="1" y2="0">
              <stop offset="0%" stop-color="var(--gradient-stop-1)"></stop>
              <stop offset="10%" stop-color="var(--gradient-stop-2)"></stop>
              <stop offset="25%" stop-color="var(--gradient-stop-3)"></stop>
              <stop offset="50%" stop-color="var(--gradient-stop-4)"></stop>
            </linearGradient>
            <mask id=mask x=0 y=0 width=120 height=25>
              <path id=timeseries></path>
            </mask>
          </defs>
          <rect x=0 y=0 width=120 height=25></rect>
        </svg>
      </sl-resize-observer>
    `;
  }

  private _updateChart() {
    const data = this._dataTask.value;
    const chartBoundingBox = this._chart?.getBoundingClientRect();
    const chart = this._chart;

    if (!data?.length || !chartBoundingBox || !chartBoundingBox.width || !chartBoundingBox.height || !chart)
      return;

    const WIDTH = chartBoundingBox.width;
    const HEIGHT = chartBoundingBox.height;

    const PADDING_TOP = 1;
    const PADDING_RIGHT = 1;
    const PADDING_BOTTOM = 1;
    const PADDING_LEFT = 1;

    const xScale = d3.scaleLinear<number, number>()
      .domain([0, data.length - 1])
      .range([PADDING_LEFT, WIDTH - PADDING_RIGHT]);
    
    const yExtent = d3.extent(data) as [number, number];
    const yMaxExtent = Math.abs(yExtent[1]) < 1e-9 ? 1 : Math.ceil(yExtent[1]*100 * 1.3)/100;
    const yScale = d3.scaleLinear<number>()
      .domain([0, yMaxExtent])
      .range([HEIGHT - PADDING_BOTTOM, PADDING_TOP]);

    const lineGenerator = d3.line<number>()
      .x((d, i) => xScale(i))
      .y(d => yScale(d))
      .curve(d3.curveNatural);

    d3.select(chart)
      .select('#timeseries')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', 'green')
      .attr('stroke-width', '2')
      .attr('d', lineGenerator);
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
      flex: none;

      --gradient-stop-1: #aceebb;
      --gradient-stop-2: #4ac26b;
      --gradient-stop-3: #2da44e;
      --gradient-stop-4: #116329;
    }

    rect {
      stroke: none;
      fill: url(#gradient);
      mask: url(#mask);
    }

    path {
      stroke: #8cc665;
    }

    #chart {
      height: 100%;
      width: 100%;
      position: relative;
    }
  `];
}

